import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import {
  AdminCompleteFailedPartnerDriverStep,
  AdminCompletePartnerDriverBackgroundCheck,
  AdminCompletePartnerDriverDotNumber,
  AdminCompletePartnerDriverInsuranceDeclaration,
  AdminCompletePartnerDriverLicense,
  AdminCompletePartnerDriverLmsTraining,
  AdminCompletePartnerDriverVehicleInsurance,
  AdminCompletePartnerDriverVehicleRegistration,
  AdminDeletePartnerDriverStepDocuments,
  AdminResetPartnerDriverStep,
  AdminSkipPartnerDriverStep,
  AdminUploadPartnerDriverStepDocuments,
} from 'src/constants/actionTypes'
import {
  BACKGROUND_CHECK_MAX_FILES,
  INSURANCE_DECLARATION_MAX_FILES,
  STEP_DOCUMENT_MAX_FILES,
  VEHICLE_REGISTRATION_MAX_FILES,
} from 'src/constants/partnerDriver/constants'
import { DotNumberOption, StepAction } from 'src/constants/partnerDriver/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { PartnerDriverStep } from 'src/interfaces/partnerDriver'
import { getPartnerDriverSaving } from 'src/selectors/partnerDriver'
import { getMedicalCardRequirement } from 'src/utils/partnerDriver/medicalCardRequirement'
import { stepDisplayLabel } from 'src/utils/partnerDriver/stepDisplayLabel'
import type { Action } from 'src/utils/reduxUtils'
import { toCompleteDotNumberPayload } from './CompleteDotNumberForm.mapper'
import type { CompleteDotNumberFormValues } from './CompleteDotNumberForm.interface'
import { CompleteDotNumberModal } from './CompleteDotNumberModal'
import { CompleteDriverLicenseModal } from './CompleteDriverLicenseModal'
import type { CompleteDriverLicenseFormValues } from './CompleteDriverLicenseForm.interface'
import { toCompleteVehicleInsurancePayload } from './CompleteVehicleInsuranceForm.mapper'
import type { CompleteVehicleInsuranceFormValues } from './CompleteVehicleInsuranceForm.interface'
import { CompleteVehicleInsuranceModal } from './CompleteVehicleInsuranceModal'
import { StepFilesModal } from './StepFilesModal'

interface Props {
  partnerDriverId: string
  action: StepAction
  step: PartnerDriverStep
  onClose(): void
  onChanged(): void
}

/**
 * Opens the one modal the chosen action needs and owns the dispatch → toast → close → refetch
 * sequence, so the modals themselves stay presentational. Rendered only while an action is
 * selected, so every form starts empty.
 */
const StepActionModalComponent = ({ partnerDriverId, action, step, onClose, onChanged }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const isSaving = useSelector(getPartnerDriverSaving)
  const label = stepDisplayLabel(step)

  const medicalCardRequirement = getMedicalCardRequirement(step)

  const run = useCallback(
    async (
      request: Action<unknown>,
      successMessage: string,
      { refreshOnFailure = false }: { refreshOnFailure?: boolean } = {},
    ) => {
      const result = await asyncDispatchWithErrorToast<boolean>(request)

      // Failure already showed its toast; the modal stays open with its values. Some actions
      // also refetch the steps then, since the backend may have changed the step anyway.
      if (!result) {
        if (refreshOnFailure) {
          onChanged()
        }

        return
      }

      snackbar?.enqueueSnackbar(successMessage, { variant: 'success' })
      onClose()
      onChanged()
    },
    [asyncDispatchWithErrorToast, snackbar, onClose, onChanged],
  )

  const handleVehicleInsuranceSubmit = useCallback(
    (values: CompleteVehicleInsuranceFormValues) => {
      const payload = toCompleteVehicleInsurancePayload(step.id, values)

      if (!payload) {
        return
      }

      run(AdminCompletePartnerDriverVehicleInsurance.request(payload), `${label} completed`)
    },
    [run, step.id, label],
  )

  const handleDriverLicenseSubmit = useCallback(
    (values: CompleteDriverLicenseFormValues) => {
      if (!values.file) {
        return
      }

      run(
        AdminCompletePartnerDriverLicense.request({
          stepId: step.id,
          file: values.file,
          expiresAt: values.expiresAt,
        }),
        `${label} completed`,
      )
    },
    [run, step.id, label],
  )

  const handleDotNumberSubmit = useCallback(
    (values: CompleteDotNumberFormValues) => {
      if (values.option === DotNumberOption.NoNumber) {
        run(AdminSkipPartnerDriverStep.request({ stepId: step.id }), `${label} skipped`)

        return
      }

      const payload = toCompleteDotNumberPayload(step.id, values, medicalCardRequirement)

      if (!payload) {
        return
      }

      run(AdminCompletePartnerDriverDotNumber.request(payload), `${label} completed`)
    },
    [run, step.id, label, medicalCardRequirement],
  )

  switch (action) {
    case StepAction.Reset:
      return (
        <ConfirmActionModal
          isOpen
          title={`Reset ${label}?`}
          description="A new step is created and the driver completes it again."
          confirmLabel="Reset step"
          isDestructive
          isSubmitting={isSaving}
          onConfirm={() => {
            run(
              AdminResetPartnerDriverStep.request({ partnerDriverId, stepId: step.id }),
              `${label} reset`,
            )
          }}
          onClose={onClose}
          confirmDataTestId="partner-driver-step-reset-confirm-button"
        />
      )
    case StepAction.CompleteFailed:
      return (
        <ConfirmActionModal
          isOpen
          title={`Complete ${label}?`}
          description={`Mark the failed ${label} step as complete.`}
          confirmLabel="Complete step"
          isSubmitting={isSaving}
          onConfirm={() => {
            run(
              AdminCompleteFailedPartnerDriverStep.request({ stepId: step.id }),
              `${label} completed`,
            )
          }}
          onClose={onClose}
          confirmDataTestId="partner-driver-step-complete-failed-confirm-button"
        />
      )
    case StepAction.DeleteDocuments: {
      const documents = step.partnerDriverCollectedData?.documents ?? []

      return (
        <ConfirmActionModal
          isOpen
          title={`Delete all documents for ${label}?`}
          description="Every document on this step will be removed."
          details={
            <ul css={tw`m-0 flex flex-col gap-1 pl-5`}>
              {documents.map((stepDocument) => {
                return (
                  <li key={stepDocument.id}>
                    <Typography variant="body2">{stepDocument.fileName}</Typography>
                  </li>
                )
              })}
            </ul>
          }
          confirmLabel={
            documents.length === 1 ? 'Delete 1 document' : `Delete ${documents.length} documents`
          }
          isDestructive
          isSubmitting={isSaving}
          onConfirm={() => {
            run(
              AdminDeletePartnerDriverStepDocuments.request({ stepId: step.id }),
              'Documents deleted',
            )
          }}
          onClose={onClose}
          confirmDataTestId="partner-driver-step-delete-documents-confirm-button"
        />
      )
    }
    case StepAction.UploadDocuments:
      return (
        <StepFilesModal
          isOpen
          title={`Upload documents for ${label}`}
          description="The files are added to this step's documents."
          submitLabel="Upload"
          maxFiles={STEP_DOCUMENT_MAX_FILES}
          isSubmitting={isSaving}
          onSubmit={(files) => {
            run(
              AdminUploadPartnerDriverStepDocuments.request({ stepId: step.id, files }),
              'Documents uploaded',
            )
          }}
          onClose={onClose}
        />
      )
    case StepAction.CompleteBackgroundCheck:
      return (
        <StepFilesModal
          isOpen
          title={`Complete ${label}`}
          description="Upload the report to mark this step complete. It goes to internal approval."
          submitLabel="Upload and complete"
          maxFiles={BACKGROUND_CHECK_MAX_FILES}
          isSubmitting={isSaving}
          onSubmit={(files) => {
            run(
              AdminCompletePartnerDriverBackgroundCheck.request({ stepId: step.id, files }),
              `${label} completed`,
            )
          }}
          onClose={onClose}
        />
      )
    case StepAction.CompleteLmsTraining:
      return (
        <StepFilesModal
          isOpen
          title={`Complete ${label}`}
          description="Upload the certificate to mark this training complete."
          submitLabel="Upload and complete"
          maxFiles={1}
          isSubmitting={isSaving}
          onSubmit={(files) => {
            run(
              AdminCompletePartnerDriverLmsTraining.request({ stepId: step.id, files }),
              `${label} completed`,
            )
          }}
          onClose={onClose}
        />
      )
    case StepAction.CompleteInsuranceDeclaration:
      return (
        <StepFilesModal
          isOpen
          title={`Complete ${label}`}
          description="Upload the declaration to mark this step complete. It goes to internal approval."
          submitLabel="Upload and complete"
          maxFiles={INSURANCE_DECLARATION_MAX_FILES}
          isSubmitting={isSaving}
          onSubmit={(files) => {
            run(
              AdminCompletePartnerDriverInsuranceDeclaration.request({ stepId: step.id, files }),
              `${label} completed`,
            )
          }}
          onClose={onClose}
        />
      )
    case StepAction.CompleteVehicleRegistration:
      return (
        <StepFilesModal
          isOpen
          title={`Complete ${label}`}
          description="Upload the vehicle registration to mark this step complete."
          submitLabel="Upload and complete"
          maxFiles={VEHICLE_REGISTRATION_MAX_FILES}
          isSubmitting={isSaving}
          onSubmit={(files) => {
            run(
              AdminCompletePartnerDriverVehicleRegistration.request({ stepId: step.id, files }),
              `${label} completed`,
              { refreshOnFailure: true },
            )
          }}
          onClose={onClose}
        />
      )
    case StepAction.CompleteDotNumber:
      return (
        <CompleteDotNumberModal
          isOpen
          stepLabel={label}
          isOptional={step.optional}
          medicalCardRequirement={medicalCardRequirement}
          isSubmitting={isSaving}
          onSubmit={handleDotNumberSubmit}
          onClose={onClose}
        />
      )
    case StepAction.CompleteVehicleInsurance:
      return (
        <CompleteVehicleInsuranceModal
          isOpen
          stepLabel={label}
          isSubmitting={isSaving}
          onSubmit={handleVehicleInsuranceSubmit}
          onClose={onClose}
        />
      )
    case StepAction.CompleteDriverLicense:
      return (
        <CompleteDriverLicenseModal
          isOpen
          stepLabel={label}
          isSubmitting={isSaving}
          onSubmit={handleDriverLicenseSubmit}
          onClose={onClose}
        />
      )
    default:
      return null
  }
}

export const StepActionModal = memo(StepActionModalComponent)
